import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Truck,
  Package,
  Send,
  CheckCircle,
  Clock,
  MapPin,
  RotateCcw,
  Calendar,
  User,
  Building,
  FileText,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

const statusConfig: Record<string, { color: string; icon: any }> = {
  PENDING: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  PICKED: { color: 'bg-amber-100 text-amber-700', icon: Package },
  PACKED: { color: 'bg-blue-100 text-blue-700', icon: Package },
  SHIPPED: { color: 'bg-purple-100 text-purple-700', icon: Send },
  IN_TRANSIT: { color: 'bg-cyan-100 text-cyan-700', icon: Truck },
  DELIVERED: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  RETURNED: { color: 'bg-red-100 text-red-700', icon: RotateCcw },
};

export function DeliveryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrierName, setCarrierName] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const {
    data: delivery,
    isLoading,
    refetch,
  } = trpc.tradingDelivery.byId.useQuery(id!, { enabled: !!id });

  const utils = trpc.useUtils();

  const handleDownloadPdf = async () => {
    if (!id) return;
    setPdfDownloading(true);
    try {
      const result = await utils.tradingDelivery.downloadPdf.fetch(id);
      const bin = atob(result.contentBase64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      showErrorToast(err?.message ?? 'Failed to download PDF');
    } finally {
      setPdfDownloading(false);
    }
  };

  const markShippedMutation = trpc.tradingDelivery.markShipped.useMutation({
    onSuccess: () => {
      utils.tradingDelivery.byId.invalidate(id);
      utils.tradingDelivery.list.invalidate();
      showSuccessToast(t('sales.deliveries.markedShipped'));
      setShipModalOpen(false);
      refetch();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const markDeliveredMutation = trpc.tradingDelivery.markDelivered.useMutation({
    onSuccess: () => {
      utils.tradingDelivery.byId.invalidate(id);
      utils.tradingDelivery.list.invalidate();
      showSuccessToast(t('sales.deliveries.markedDelivered'));
      refetch();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return t('sales.deliveries.pending');
      case 'PICKED':
        return t('sales.deliveries.picked');
      case 'PACKED':
        return t('sales.deliveries.packed');
      case 'SHIPPED':
        return t('sales.deliveries.shipped');
      case 'IN_TRANSIT':
        return t('sales.deliveries.inTransit');
      case 'DELIVERED':
        return t('sales.deliveries.delivered');
      case 'RETURNED':
        return t('sales.deliveries.returned');
      default:
        return status;
    }
  };

  const formatDate = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return '-';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatDateShort = (dateValue: string | Date | null | undefined) => {
    if (!dateValue) return '-';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const handleMarkShipped = () => {
    if (!id) return;
    markShippedMutation.mutate({
      id,
      trackingNumber: trackingNumber || undefined,
      carrierName: carrierName || undefined,
    });
  };

  const handleMarkDelivered = () => {
    if (!id) return;
    markDeliveredMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (!delivery) {
    return (
      <div className="text-center py-12">
        <Truck className="mx-auto h-12 w-12 text-muted-foreground/50" />
        <h3 className="mt-4 text-lg font-medium">{tCommon('notFound')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('sales.deliveries.notFoundDesc')}
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/trading/sales/deliveries')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('sales.deliveries.backToList')}
        </Button>
      </div>
    );
  }

  const config = statusConfig[delivery.status] || statusConfig.PENDING;
  const StatusIcon = config?.icon ?? Clock;

  const canMarkShipped =
    delivery.status === 'PENDING' ||
    delivery.status === 'PICKED' ||
    delivery.status === 'PACKED';
  const canMarkDelivered =
    delivery.status === 'SHIPPED' || delivery.status === 'IN_TRANSIT';

  const totalItems = delivery.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/trading/sales/deliveries')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-mono">
                {delivery.deliveryNumber}
              </h1>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
              >
                <StatusIcon className="h-3 w-3" />
                {getStatusLabel(delivery.status)}
              </span>
            </div>
            <p className="text-muted-foreground">
              {t('sales.deliveries.createdOn')} {formatDate(delivery.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={pdfDownloading}
          >
            <Download className="mr-2 h-4 w-4" />
            {t('sales.deliveries.downloadPdf')}
          </Button>
          {canMarkShipped && (
            <Button onClick={() => setShipModalOpen(true)}>
              <Send className="mr-2 h-4 w-4" />
              {t('sales.deliveries.markShipped')}
            </Button>
          )}
          {canMarkDelivered && (
            <Button onClick={handleMarkDelivered} disabled={markDeliveredMutation.isPending}>
              {markDeliveredMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="mr-2 h-4 w-4" />
              )}
              {t('sales.deliveries.markDelivered')}
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Delivery Info & Items */}
        <div className="space-y-6 lg:col-span-2">
          {/* Delivery Details Card */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <Truck className="h-5 w-5" />
                {t('sales.deliveries.deliveryDetails')}
              </h2>
            </div>
            <div className="p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('sales.deliveries.deliveryNumber')}
                  </Label>
                  <p className="font-mono font-medium">{delivery.deliveryNumber}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('sales.deliveries.status')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
                    >
                      <StatusIcon className="h-3 w-3" />
                      {getStatusLabel(delivery.status)}
                    </span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('sales.deliveries.warehouse')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <p>{delivery.warehouse.name}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('sales.deliveries.shippingMethod')}
                  </Label>
                  <p>{delivery.shippingMethod || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('sales.deliveries.carrier')}
                  </Label>
                  <p>{delivery.carrierName || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('sales.deliveries.trackingNumber')}
                  </Label>
                  <div className="flex items-center gap-2">
                    {delivery.trackingNumber ? (
                      <>
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono">{delivery.trackingNumber}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('sales.deliveries.shipDate')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p>{formatDateShort(delivery.shipDate)}</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">
                    {t('sales.deliveries.deliveryDate')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <p>{formatDateShort(delivery.deliveryDate)}</p>
                  </div>
                </div>
              </div>

              {delivery.notes && (
                <div className="mt-4 pt-4 border-t border-border">
                  <Label className="text-muted-foreground text-xs">
                    {t('sales.deliveries.notes')}
                  </Label>
                  <p className="mt-1 text-sm">{delivery.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Delivery Items Card */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <Package className="h-5 w-5" />
                  {t('sales.deliveries.deliveryItems')}
                </h2>
                <span className="text-sm text-muted-foreground">
                  {delivery.items.length} {t('sales.deliveries.items')} ({totalItems}{' '}
                  {t('sales.deliveries.units')})
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('sales.deliveries.product')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      {t('sales.deliveries.productCode')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium">
                      {t('sales.deliveries.quantity')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {delivery.items.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <p className="font-medium">{item.productName}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-muted-foreground">
                          {item.productCode}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium">{item.quantity}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/30">
                  <tr>
                    <td colSpan={2} className="px-4 py-3 text-right font-medium">
                      {t('sales.deliveries.total')}
                    </td>
                    <td className="px-4 py-3 text-right font-bold">{totalItems}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column - Related Info */}
        <div className="space-y-6">
          {/* Sales Order Card */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <FileText className="h-5 w-5" />
                {t('sales.deliveries.salesOrder')}
              </h2>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">
                  {t('sales.orders.orderNumber')}
                </Label>
                <Link
                  to={`/trading/sales/orders/${delivery.salesOrderId}`}
                  className="block font-mono font-medium text-primary hover:underline"
                >
                  {delivery.salesOrder.orderNumber}
                </Link>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">
                  {t('sales.orders.customer')}
                </Label>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <p>
                    {delivery.salesOrder.tradingCustomer?.companyName ||
                      delivery.salesOrder.companyName ||
                      delivery.salesOrder.contactName ||
                      '-'}
                  </p>
                </div>
              </div>
              {delivery.salesOrder.tradingCustomer && (
                <div className="pt-2">
                  <Link
                    to={`/trading/customers/${delivery.salesOrder.tradingCustomer.id}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {t('sales.deliveries.viewCustomer')}
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Created By Card */}
          {delivery.createdBy && (
            <div className="border border-border bg-card">
              <div className="p-4 border-b border-border">
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  <User className="h-5 w-5" />
                  {t('sales.deliveries.createdBy')}
                </h2>
              </div>
              <div className="p-4 space-y-2">
                <p className="font-medium">
                  {delivery.createdBy.firstName} {delivery.createdBy.lastName}
                </p>
                <p className="text-sm text-muted-foreground">{delivery.createdBy.email}</p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(delivery.createdAt)}
                </p>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="border border-border bg-card">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold">{t('sales.deliveries.quickActions')}</h2>
            </div>
            <div className="p-4 space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/trading/sales/orders/${delivery.salesOrderId}`)}
              >
                <FileText className="mr-2 h-4 w-4" />
                {t('sales.deliveries.viewSalesOrder')}
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/trading/sales/deliveries')}
              >
                <Truck className="mr-2 h-4 w-4" />
                {t('sales.deliveries.backToList')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mark Shipped Modal */}
      <Dialog open={shipModalOpen} onOpenChange={setShipModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sales.deliveries.markShipped')}</DialogTitle>
            <DialogDescription>
              {t('sales.deliveries.markShippedDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="trackingNumber">{t('sales.deliveries.trackingNumber')}</Label>
              <Input
                id="trackingNumber"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder={t('sales.deliveries.trackingPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="carrierName">{t('sales.deliveries.carrier')}</Label>
              <Input
                id="carrierName"
                value={carrierName}
                onChange={(e) => setCarrierName(e.target.value)}
                placeholder="e.g., DHL, FedEx, UPS"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShipModalOpen(false)}>
              {tCommon('cancel')}
            </Button>
            <Button onClick={handleMarkShipped} disabled={markShippedMutation.isPending}>
              {markShippedMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {t('sales.deliveries.confirmShipped')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DeliveryDetailPage;
